"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface TagManageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTags: string[];
  onSave: (tags: string[]) => void;
}

const rubberband = {
  initial: { opacity: 0, scale: 0.5 },
  animate: {
    opacity: 1,
    scale: [0.5, 1.08, 0.97, 1],
    transition: { duration: 0.3, ease: "easeOut" as const },
  },
};

const exitAnim = {
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.12, ease: "easeIn" as const } },
};

export function TagManageSheet({ open, onOpenChange, currentTags, onSave }: TagManageSheetProps) {
  const t = useTranslations("recipes");
  const [selectedTags, setSelectedTags] = useState<string[]>(currentTags);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const initialSelectedRef = useRef(new Set<string>());
  const returnedTagsRef = useRef(new Set<string>());

  useEffect(() => {
    if (open) {
      setSelectedTags(currentTags);
      setSearch("");
      initialSelectedRef.current = new Set(currentTags);
      returnedTagsRef.current = new Set<string>();
      setLoading(true);
      fetch("/api/tags/available")
        .then((r) => r.json())
        .then((data) => setAvailableTags(data.tags ?? []))
        .catch(() => setAvailableTags([]))
        .finally(() => setLoading(false));
    }
  }, [open, currentTags]);

  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

  const filteredUnselected = useMemo(() => {
    const unselected = availableTags.filter((t) => !selectedSet.has(t));
    if (!search.trim()) return unselected;
    const q = search.trim().toLowerCase();
    return unselected.filter((t) => t.toLowerCase().includes(q));
  }, [availableTags, selectedSet, search]);

  const addTag = useCallback((tag: string) => {
    setSelectedTags((prev) => [...prev, tag]);
  }, []);

  const removeTag = useCallback((tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
    returnedTagsRef.current.add(tag);
  }, []);

  const handleSave = () => {
    onSave(selectedTags);
    onOpenChange(false);
  };

  const isNewSelected = (tag: string) => !initialSelectedRef.current.has(tag);
  const isReturnedAvailable = (tag: string) => returnedTagsRef.current.has(tag);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t("manageTags")}</SheetTitle>
          <SheetDescription>{t("manageTagsDescription")}</SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5 overflow-hidden">
          <section className="shrink-0">
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">{t("yourTags")}</h3>
            {selectedTags.length === 0 ? (
              <p className="text-sm text-muted-foreground/60">{t("yourTagsEmpty")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <AnimatePresence mode="popLayout">
                  {selectedTags.map((tag) => (
                    <motion.button
                      key={tag}
                      type="button"
                      layout="position"
                      initial={isNewSelected(tag) ? rubberband.initial : false}
                      animate={isNewSelected(tag) ? rubberband.animate : { opacity: 1, scale: 1 }}
                      {...exitAnim}
                      onClick={() => removeTag(tag)}
                      aria-label={t("removeTag", { tag })}
                      className={cn(
                        "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-foreground bg-foreground px-3 py-1.5 text-sm font-medium text-background",
                        "transition-colors hover:bg-foreground/80"
                      )}
                    >
                      {tag}
                      <X className="size-3.5" aria-hidden />
                    </motion.button>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </section>

          <section className="flex min-h-0 flex-1 flex-col">
            <h3 className="mb-3 shrink-0 text-sm font-medium text-muted-foreground">
              {t("availableTags")}
            </h3>

            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("searchTagsPlaceholder")}
              size="sm"
              className="mx-px mb-3 shrink-0"
            />

            <div className="h-[200px] overflow-y-auto scrollbar-thin">
              {loading ? (
                <div className="flex gap-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-8 w-16 animate-pulse rounded-full bg-muted" />
                  ))}
                </div>
              ) : filteredUnselected.length === 0 ? (
                <p className="text-sm text-muted-foreground/60">{t("availableTagsEmpty")}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {filteredUnselected.map((tag) => {
                    const shouldAnimate = isReturnedAvailable(tag);
                    return shouldAnimate ? (
                      <motion.button
                        key={tag}
                        type="button"
                        {...rubberband}
                        onClick={() => {
                          returnedTagsRef.current.delete(tag);
                          addTag(tag);
                        }}
                        aria-label={t("addTag", { tag })}
                        className={cn(
                          "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-input-border px-3 py-1.5 text-sm font-medium text-muted-foreground",
                          "transition-colors hover:bg-muted hover:text-foreground"
                        )}
                      >
                        <Plus className="size-3.5" aria-hidden />
                        {tag}
                      </motion.button>
                    ) : (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        aria-label={t("addTag", { tag })}
                        className={cn(
                          "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-input-border px-3 py-1.5 text-sm font-medium text-muted-foreground",
                          "transition-colors hover:bg-muted hover:text-foreground"
                        )}
                      >
                        <Plus className="size-3.5" aria-hidden />
                        {tag}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </SheetBody>

        <SheetFooter className="rounded-b-2xl">
          <Button onClick={handleSave} className="w-full sm:w-auto">
            {t("done")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
